import { useCallback, useEffect, useSyncExternalStore } from "react";
import { AvatarResponse, EMPTY_AVATAR, getMyAvatar } from "../api/avatar.api";

/**
 * Tiny shared store for the signed-in user's avatar so the Sidebar and the
 * editor show the same thing and a save is reflected everywhere at once.
 * Keyed by user id: switching accounts refetches instead of leaking.
 */
type Status = "idle" | "loading" | "ready" | "error";
interface StoreState {
  userId: number | null;
  avatar: AvatarResponse | null;
  status: Status;
}

let state: StoreState = { userId: null, avatar: null, status: "idle" };
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;

const emit = (next: Partial<StoreState>) => {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => state;

const load = (userId: number): Promise<void> => {
  if (inflight && state.userId === userId) return inflight;
  emit({ userId, status: "loading", avatar: state.userId === userId ? state.avatar : null });
  const p = getMyAvatar()
    .then((avatar) => {
      if (state.userId === userId) emit({ avatar: avatar ?? EMPTY_AVATAR, status: "ready" });
    })
    .catch(() => {
      if (state.userId === userId) emit({ status: "error" });
    })
    .finally(() => {
      if (inflight === p) inflight = null;
    });
  inflight = p;
  return p;
};

/** Replace the cached avatar after a successful PUT/DELETE. */
export const setMyAvatar = (avatar: AvatarResponse) => emit({ avatar, status: "ready" });

/** Test/logout helper. */
export const resetMyAvatarStore = () => {
  inflight = null;
  state = { userId: null, avatar: null, status: "idle" };
  listeners.forEach((l) => l());
};

export function useMyAvatar(userId?: number | null) {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (userId == null) return;
    if (state.userId !== userId || state.status === "idle") void load(userId);
  }, [userId]);
  const reload = useCallback(() => (userId == null ? Promise.resolve() : load(userId)), [userId]);
  const mine = userId != null && s.userId === userId;
  return {
    avatar: mine ? s.avatar : null,
    status: mine ? s.status : ("idle" as Status),
    reload,
    setAvatar: setMyAvatar,
  };
}
